import React from 'react';
import { Box, Button, SvgIcon, Typography } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import getMuiIcon from '../../utils/getMuiIcon';
import useSubscriptionInfoData, { SubscriptionStatus } from '../../utils/useSubscriptionInfoData';
import SubscriptionStatusAlert from './SubscriptionStatusAlert';

interface SubscriptionDetailsProps {
  isBasicUserWithoutSubscription: boolean;
  endDate: string;
  subscriptionStatus: SubscriptionStatus;
  onClickUpgrade: () => void;
  onShowManageSubscriptionDialog: () => void;
  onManageStripeBilling?: () => void;
  isCreatingStripePortalSession?: boolean;
}

const SubscriptionDetails = ({
  isBasicUserWithoutSubscription,
  endDate,
  subscriptionStatus,
  onClickUpgrade,
  onShowManageSubscriptionDialog,
  onManageStripeBilling,
  isCreatingStripePortalSession,
}: SubscriptionDetailsProps) => {
  const { t } = useTranslation();
  const {
    isBusinessUser,
    allowAutomaticCancellation,
    productName,
    price,
    priceText,
    discountText,
    discountMonths,
    reducedPrice,
    pauseStartDate,
    pauseEndDate,
    isInTrial,
    withdrawalSubmittedAt,
    isStripeSubscriptionTerminated,
  } = useSubscriptionInfoData();

  if (isBasicUserWithoutSubscription) {
    return (
      <Grid item>
        <Box sx={{ display: 'grid', gap: spaces.lg }}>
          <Typography
            variant='body1'
            color={palette.text.disabled}
          >
            {t('No active subscription')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spaces.md }}>
            {/* SM-388: a terminated Stripe subscriber (routed here by SM-383's
                basic-user fall-through) has already been downgraded to Basis but
                still owns a Stripe customer with billing history — surface the
                billing-portal button so they can reach past invoices/payments.
                A never-Stripe basic user has no portal, so gate on the terminated
                flag. */}
            {isStripeSubscriptionTerminated && onManageStripeBilling && (
              <Button
                variant='contained'
                color='primary'
                size='large'
                onClick={onManageStripeBilling}
                disabled={isCreatingStripePortalSession}
                data-testid='stripe-billing-portal-button'
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  width: 'fit-content',
                  paddingX: spaces.lg,
                  paddingY: spaces.xs,
                }}
              >
                {t('Invoices & Payments')}
              </Button>
            )}
            <Button
              variant='contained'
              size='large'
              color='primary'
              key='upgrade-button'
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: 'fit-content',
                paddingX: spaces.lg,
                paddingY: spaces.xs,
              }}
              startIcon={
                <SvgIcon
                  sx={{ marginRight: spaces.xs }}
                  component={getMuiIcon('upgradeSubscription')}
                  viewBox='0 0 24 24'
                />
              }
              onClick={onClickUpgrade}
            >
              {t('Upgrade Plan')}
            </Button>
          </Box>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item>
      <Box>
        <Typography
          variant='h6'
          color={palette.text.primary}
          sx={{
            marginBottom: spaces.xxs,
          }}
        >
          {discountText ? `${productName} — ${t('discount', { discountText })}` : productName}
        </Typography>
        <Typography>
          {t(priceText, { discountText, discountMonths, price, reducedPrice })}
        </Typography>
        {subscriptionStatus === SubscriptionStatus.Active && allowAutomaticCancellation && (
          <Button
            variant='outlined'
            color='inherit'
            size='large'
            onClick={onShowManageSubscriptionDialog}
            sx={{
              marginTop: spaces.xl,
            }}
          >
            {isInTrial ? t('Cancel Trial Subscription') : t('Manage Subscription')}
          </Button>
        )}
        <SubscriptionStatusAlert
          subscriptionStatus={subscriptionStatus}
          endDate={endDate}
          pauseStartDate={pauseStartDate}
          pauseEndDate={pauseEndDate}
          isBusinessUser={isBusinessUser}
          withdrawalSubmittedAt={withdrawalSubmittedAt}
        />
      </Box>
    </Grid>
  );
};

export default SubscriptionDetails;
